"use client";

import type {
	PayoutsSessionOptions,
	PayoutsSession as PayoutsSessionType,
} from "@whop/embedded-components-vanilla-js/types";
import type { PropsWithChildren } from "react";

import React, {
	createContext,
	useContext,
	useEffect,
	useImperativeHandle,
	useMemo,
} from "react";
import { useElements } from "../provider";

interface ContextState {
	payoutsSession: PayoutsSessionType | null;
}

const PayoutsSessionContext = createContext<ContextState | null>(null);

export function usePayoutsSession() {
	const ctx = useContext(PayoutsSessionContext);
	if (!ctx) {
		throw new Error(
			"usePayoutsSession must be used within a PayoutsSessionProvider",
		);
	}
	return ctx.payoutsSession;
}

export function PayoutsSession({
	children,
	currency,
	companyId,
	token,
	redirectUrl,
	ref,
}: PropsWithChildren<
	PayoutsSessionOptions & {
		ref?: React.RefObject<{
			payoutsSession: PayoutsSessionType | null;
		} | null>;
	}
>) {
	const elements = useElements();

	const payoutsSession = useMemo(() => {
		if (!elements) return null;
		return elements.createPayoutsSession({
			token,
			currency,
			companyId,
			redirectUrl,
		});
	}, [elements]);

	useEffect(() => {
		if (!payoutsSession) return;

		payoutsSession.updateOptions({
			token,
			currency,
			companyId,
			redirectUrl,
		});
	}, [payoutsSession, currency, token, companyId, redirectUrl]);

	const value: ContextState = useMemo(
		() => ({
			payoutsSession,
		}),
		[payoutsSession],
	);

	useImperativeHandle(ref, () => ({ payoutsSession }), [payoutsSession]);

	return (
		<PayoutsSessionContext.Provider value={value}>
			{children}
		</PayoutsSessionContext.Provider>
	);
}
